import type { ApiEnvelope, ApiError } from "@opus-dt/shared-types";
import { useAuthStore } from "@/stores/auth.store";

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: ApiError
  ) {
    super(error.message);
    this.name = "ApiClientError";
  }
}

// `instanceof` primeiro; fallback estrutural robusto a duplicação de módulo / HMR.
// Exige `status:number` + `error.message:string` (a forma real de ApiClientError) para que
// callers possam ler `.status`/`.error.code` com segurança — não casa qualquer `{error:{message}}`.
export function isApiClientError(e: unknown): e is ApiClientError {
  if (e instanceof ApiClientError) return true;
  return (
    !!e &&
    typeof e === "object" &&
    "status" in e &&
    typeof (e as { status?: unknown }).status === "number" &&
    "error" in e &&
    typeof (e as { error?: { message?: unknown } }).error?.message === "string"
  );
}

// Extrator canônico de mensagem de erro — toda UI que precisa mostrar "o que
// aconteceu" ao usuário passa por aqui, nunca por `err.message`/`err instanceof Error` direto
// (isso vazaria texto técnico cru de erro de rede/JS quando `err` não é um ApiClientError).
export function getFriendlyErrorMessage(err: unknown, fallback = "Erro inesperado."): string {
  return isApiClientError(err) ? err.error.message : fallback;
}

interface ApiClientOptions {
  baseUrl?: string;
}

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  body?: unknown;
}

const NO_REFRESH_RETRY_PATHS = [
  "/api/v1/auth/login",
  "/api/v1/auth/refresh",
  "/api/v1/auth/logout",
];

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

function setAccessToken(token: string | null) {
  accessToken = token;
}

function getAccessToken(): string | null {
  return accessToken;
}

async function refreshAccessToken(baseUrl: string): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return null;
      const body = (await res.json()) as ApiEnvelope<{ accessToken: string }>;
      const newToken = body.data?.accessToken ?? null;
      accessToken = newToken;
      return newToken;
    } catch {
      return null;
    }
  })();
  const result = await refreshPromise;
  refreshPromise = null;
  return result;
}

async function rawRequest<T>(
  method: string,
  path: string,
  options: RequestOptions & { baseUrl: string }
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
    signal: options.signal,
  };
  if (options.body !== undefined) {
    init.body = options.body instanceof FormData ? options.body : JSON.stringify(options.body);
  }

  const url = `${options.baseUrl}${path}`;
  let res = await fetch(url, init);

  if (res.status === 401 && !NO_REFRESH_RETRY_PATHS.some((p) => path.startsWith(p))) {
    const newToken = await refreshAccessToken(options.baseUrl);
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      res = await fetch(url, { ...init, headers });
    }
    // Refresh falhou (ou a sessão foi revogada e a chamada repetida ainda veio 401):
    // limpa a sessão fora do boot. `ProtectedLayout` já reage a `user===null` (mesmo caminho do
    // `useMe`), então isso basta para redirecionar a `/login` sem navegação imperativa aqui.
    if (!newToken || res.status === 401) {
      useAuthStore.getState().clearSession();
    }
  }

  const contentLength = res.headers.get("content-length");
  const contentType = res.headers.get("content-type") ?? "";
  const hasJsonBody =
    res.status !== 204 &&
    contentLength !== "0" &&
    contentType.toLowerCase().startsWith("application/json");

  if (!hasJsonBody) {
    if (!res.ok) {
      throw new ApiClientError(res.status, {
        code: "INTERNAL_ERROR",
        message: `HTTP ${res.status}`,
      });
    }
    return undefined as T;
  }

  const body = (await res.json().catch(() => ({
    data: null,
    error: {
      code: "INTERNAL_ERROR",
      message: "Resposta não é JSON.",
    },
  }))) as ApiEnvelope<T>;

  if (!res.ok || body.error) {
    throw new ApiClientError(
      res.status,
      body.error ?? {
        code: "INTERNAL_ERROR",
        message: "Erro desconhecido.",
      }
    );
  }
  return body.data as T;
}

export function createApiClient(opts: ApiClientOptions = {}) {
  const baseUrl = opts.baseUrl ?? "";

  function methodCall(method: string) {
    return <T>(path: string, body?: unknown, options?: RequestOptions) =>
      rawRequest<T>(method, path, { ...options, baseUrl, body });
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      rawRequest<T>("GET", path, { ...options, baseUrl }),
    post: methodCall("POST"),
    patch: methodCall("PATCH"),
    put: methodCall("PUT"),
    delete: methodCall("DELETE"),
    setAccessToken,
    getAccessToken,
    // Repõe o access token em memória a partir do cookie de refresh (usado no boot/F5: o token de
    // módulo é volátil, mas o WebSocket e o Bearer precisam dele — ver useMe).
    refresh: () => refreshAccessToken(baseUrl),
  };
}

export const api = createApiClient();

export { rawRequest, refreshAccessToken };
